import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types';
import Dropzone from 'components/Dropzone'
import UploadService from 'services/UploadService';
import UploadFilePopup from './popup/uploadFile/UploadFilePopup';
import getObjectValue from 'libs/getObjectValue';
import removeFormikListElement from 'libs/removeFormikListElement';
import TitledComponent from './TitledComponent';
import {makeStyles} from '@material-ui/core';
import FilesList from "./FilesList";
import {connect} from 'react-redux';
import PrivilegeService from "../services/PrivilegeService";
import GridBlock from "./GridBlock";
import SharepointExplorer from "./complex/sharepointExplorer/SharepointExplorer";
import Tr from "../translations/Translations";
import SharepointIcon from "./icon/SharepointIcon";
import Button from "./Button";
import SharepointDownloadPopup from "./complex/sharepointExplorer/SharepointDownloadPopup";
import {useParams} from "react-router";
import EzdrpExplorer from "./complex/ezdrpExplorer/EzdrpExplorer";
import EzdrpDownloadPopup from "./complex/ezdrpExplorer/EzdrpDownloadPopup";
import EzdpuwDownloadPopup from "./complex/ezdpuwExplorer/EzdpuwDownloadPopup";
import EzdpuwExplorer from "./complex/ezdpuwExplorer/EzdpuwExplorer";

const useStyle = makeStyles(theme => ({
    dropzone: {
        marginTop: theme.spacing(1)
    },
    dropzoneParent: {
        '&>*:first-child': {
            flexGrow: 1,
        },
        '&>*:nth-child(2)': {
            marginLeft: theme.spacing(2),
            marginTop: theme.spacing()
        }
    },
    integrationButton: {
        width: "100%",
        marginBottom: theme.spacing()
    }
}));

const SupportedTypes = {
    BROWSER: "BROWSER",
    SHAREPOINT: "SHAREPOINT",
    EZDRP: "EZDRP",
    EZDPUW: "EZDPUW",
}

function UploadFile(
    {
        //common
        label,
        onUpload,
        onUploadPopupClosed,
        acceptExtensionList,
        disabled,
        working,
        labelClassName,

        //formikProps
        multiple = true,
        id,
        formikProps,
        onDelete,
        onDownload,
        onOpenConfigureFiles,
        configuredFiles,
        defaultValidExtensionList,
        demandHeader,
        demandPrivileges = {},
        encryptRoundId
    }) {
    const params = useParams();
    const demandId = params.id;

    const classes = useStyle();
    const editable = formikProps ? getObjectValue('_editable', formikProps.values, true) : true;

    const currentProcess = useRef(null);

    const [browserDownloadFiles, setBrowserDownloadFiles] = useState(null);

    const [sharepointExplorerOpen, setSharepointExplorerOpen] = useState(false);
    const [sharepointDownloadFiles, setSharepointDownloadFiles] = useState(null);

    const [ezdrpExplorerOpen, setEzdrpExplorerOpen] = useState(false);
    const [ezdDownloadFiles, setEzdDownloadFiles] = useState(null);

    const [ezdpuwExplorerOpen, setEzdpuwExplorerOpen] = useState(false);
    const [ezdpuwDownloadFiles, setEzdpuwDownloadFiles] = useState(null);

    useEffect(() => {
        if (configuredFiles) {
            if (currentProcess.current === SupportedTypes.BROWSER)
                setBrowserDownloadFiles(configuredFiles)
            else if (currentProcess.current === SupportedTypes.SHAREPOINT)
                setSharepointDownloadFiles(configuredFiles)
            else if (currentProcess.current === SupportedTypes.EZDRP)
                setEzdDownloadFiles(configuredFiles)
            else if (currentProcess.current === SupportedTypes.EZDPUW)
                setEzdpuwDownloadFiles(configuredFiles)
            currentProcess.current = null;

        }
    }, [configuredFiles])


    const handleBrowserUpload = ({files, config}) => {
        setBrowserDownloadFiles(null);
        handleDownloadedFiles(files, config);
    };

    const handleSubmitSharepointDownloadFiles = ({files, config}) => {
        setSharepointDownloadFiles(null)
        handleDownloadedFiles(files, config);
    }

    const handleSubmitEzdrpDownloadFiles = ({files, config}) => {
        setEzdDownloadFiles(null)
        handleDownloadedFiles(files, config);
    }

    const handleSubmitEzdpuwDownloadFiles = ({files, config}) => {
        setEzdpuwDownloadFiles(null)
        handleDownloadedFiles(files, config);
    }

    const handleDownloadedFiles = (files, config) => {
        if (formikProps) {
            const formikFiles = getObjectValue(id, formikProps.values, []);
            files.forEach((fileData, i) => {
                const fileId = `${id}.${formikFiles.length + i}.file`;
                formikProps.setFieldValue(fileId, fileData)
            })
        }
        if (onUploadPopupClosed)
            onUploadPopupClosed(files, config);
    }


    const handleBrowserFileSelect = (selectedFiles) => {
        if (onOpenConfigureFiles) {
            currentProcess.current = SupportedTypes.BROWSER;
            onOpenConfigureFiles(selectedFiles);
        } else {
            setBrowserDownloadFiles({files: selectedFiles, config: null});
        }
    }

    const handleSharepointExplorerSubmit = (selectedFiles) => {
        setSharepointExplorerOpen(false);
        if (onOpenConfigureFiles) {
            currentProcess.current = SupportedTypes.SHAREPOINT;
            onOpenConfigureFiles(selectedFiles)
        } else {
            setSharepointDownloadFiles({files: selectedFiles, config: null})
        }
    }
    const handleEzdrpExplorerSubmit = (selectedFiles) => {
        setEzdrpExplorerOpen(false);
        if (onOpenConfigureFiles) {
            currentProcess.current = SupportedTypes.EZDRP;
            onOpenConfigureFiles(selectedFiles)
        } else {
            setEzdDownloadFiles({files: selectedFiles, config: null})
        }
    }
    const handleEzdpuwExplorerSubmit = (selectedFiles) => {
        setEzdpuwExplorerOpen(false);
        if (onOpenConfigureFiles) {
            currentProcess.current = SupportedTypes.EZDPUW;
            onOpenConfigureFiles(selectedFiles)
        } else {
            setEzdpuwDownloadFiles({files: selectedFiles, config: null})
        }
    }


    const deleteFile = (index) => {
        if (onDelete)
            onDelete(index);

        if (formikProps)
            removeFormikListElement(formikProps, id, index);
    };

    const fileElements = formikProps ? getObjectValue(id, formikProps.values, []) : [];

    const getIntegrationButtons = () => {
        if (demandPrivileges?.INTEGRATIONS_DOWNLOAD && !!demandId) {
            const sharepoint = demandHeader?.applicationClientSettings?.sharepointIntegration || {};
            const ezdrp = demandHeader?.applicationClientSettings?.ezdrpIntegration || {};
            const ezdpuw = demandHeader?.applicationClientSettings?.ezdpuwIntegration || {};

            if (sharepoint.active || ezdrp.active || ezdpuw.active) {
                return (<GridBlock column>
                    {
                        !!sharepoint.active && <Button
                            className={classes.integrationButton}
                            Icon={SharepointIcon}
                            onClick={() => setSharepointExplorerOpen(true)}
                            disabled={disabled || working || !sharepoint.connected}
                            tooltip={!sharepoint.connected ? Tr("int_demand_not_connected_x", {x: "sharepoint"}, true) : undefined}
                        >{Tr("int_download_from_x", {x: "sharepoint"}, true)}</Button>
                    }
                    {
                        !!ezdrp.active && <Button
                            className={classes.integrationButton}
                            onClick={() => setEzdrpExplorerOpen(true)}
                            disabled={disabled || working || !ezdrp.connected}
                            tooltip={!ezdrp.connected ? Tr("int_demand_not_connected_x", {x: "ezdrp"}, true) : undefined}
                        >{Tr("int_download_from_x", {x: "ezdrp"}, true)}</Button>
                    }
                    {
                        !!ezdpuw.active && <Button
                            className={classes.integrationButton}
                            onClick={() => setEzdpuwExplorerOpen(true)}
                            disabled={disabled || working || !ezdpuw.connected}
                            tooltip={!ezdpuw.connected ? Tr("int_demand_not_connected_x", {x: "ezdpuw"}, true) : undefined}
                        >{Tr("int_download_from_x", {x: "ezdpuw"}, true)}</Button>
                    }
                </GridBlock>)
            }
        }
        return false;
    }

    return (
        <TitledComponent
            title={label}
            labelClassName={labelClassName}
        >
            {!!formikProps && <FilesList
                elements={fileElements}
                onDelete={(editable && !disabled) ? deleteFile : undefined}
                onDownload={onDownload} />}
            {editable && <>
                <GridBlock className={classes.dropzoneParent}>
                    <Dropzone
                        className={classes.dropzone}
                        onChange={handleBrowserFileSelect}
                        multiple={multiple}
                        acceptExtensionList={acceptExtensionList ? acceptExtensionList : defaultValidExtensionList}
                        disabled={disabled}
                        working={working}
                    />
                    {
                        getIntegrationButtons()
                    }

                </GridBlock>

                {browserDownloadFiles && <UploadFilePopup
                    downloadFiles={browserDownloadFiles}
                    handleSubmit={handleBrowserUpload}
                    onUpload={onUpload}
                    encryptRoundId={encryptRoundId}
                />}


                {sharepointExplorerOpen && <SharepointExplorer
                    onClose={() => setSharepointExplorerOpen(false)}
                    handleSubmit={handleSharepointExplorerSubmit}
                    demandId={demandId}
                />}

                {ezdrpExplorerOpen && <EzdrpExplorer
                    onClose={() => setEzdrpExplorerOpen(false)}
                    handleSubmit={handleEzdrpExplorerSubmit}
                    demandId={demandId}
                />}

                {ezdpuwExplorerOpen && <EzdpuwExplorer
                    onClose={() => setEzdpuwExplorerOpen(false)}
                    handleSubmit={handleEzdpuwExplorerSubmit}
                    demandId={demandId}
                />}

                {!!sharepointDownloadFiles &&
                    <SharepointDownloadPopup
                        demandId={demandId}
                        downloadFiles={sharepointDownloadFiles}
                        handleSubmit={handleSubmitSharepointDownloadFiles}
                        onUpload={onUpload}
                    />
                }

                {!!ezdDownloadFiles &&
                    <EzdrpDownloadPopup
                        demandId={demandId}
                        downloadFiles={ezdDownloadFiles}
                        handleSubmit={handleSubmitEzdrpDownloadFiles}
                        onUpload={onUpload}
                    />
                }

                {!!ezdpuwDownloadFiles &&
                    <EzdpuwDownloadPopup
                        demandId={demandId}
                        downloadFiles={ezdpuwDownloadFiles}
                        handleSubmit={handleSubmitEzdpuwDownloadFiles}
                        onUpload={onUpload}
                    />
                }

            </>}
        </TitledComponent>
    )
}

UploadFile.propTypes = {
    onDelete: PropTypes.func,
    onDownload: PropTypes.func,

    configuredFiles: PropTypes.shape({
        files: PropTypes.array.isRequired,
        config: PropTypes.any.isRequired
    }),
    working: PropTypes.bool

};

export default connect((state) => ({
    defaultValidExtensionList: state.resources[UploadService.VALID_EXTENSIONS_LIST_RESOURCE_ID],
    demandHeader: state.resources.demandHeader,
    demandPrivileges: state.privileges[PrivilegeService.DEMAND_PRIVILEGE_STORE_KEY]
}))(UploadFile);